/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/** Raw identity inputs for building a vehicle label. */
export interface VehicleLabelInput {
  vin?: string | null;
  year?: string | null;
  make?: string | null;
  model?: string | null;
  /** Pre-joined "make model" (used when make/model aren't available separately). */
  combined?: string | null;
  programCode?: string | null;
  release?: string | null;
}

/** Label parts in display priority: VIN, vehicle details, then program code and release. */
export interface VehicleLabelParts {
  vin?: string;
  details?: string;
  code?: string;
  release?: string;
}

const clean = (s?: string | null) => (s ?? '').trim();

const NOT_A_TRIM = /^(n\/?a|none|null|undefined|-)$/i;

/** Street trim worth showing. Blank and the AVP sentinels stay off the picker. */
export function displayTrim(value?: string | null): string | undefined {
  const v = clean(value);
  if (!v || NOT_A_TRIM.test(v)) return undefined;
  return v;
}

/** Title-case catalog make/model strings for display. */
function titleCaseVehicle(raw: string): string {
  return raw
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => {
      if (/\d/.test(w)) return w; // model numbers (1500, XT4)
      if (w.length <= 2) return w.toUpperCase(); // short trims (EV, XT)
      if (w.length === 3 && w === w.toUpperCase()) return w; // acronym makes (GMC)
      return w[0].toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ');
}

/** Builds display parts from raw identity. Callers apply internal-only gating for code/release. */
export function vehicleLabel(input: VehicleLabelInput): VehicleLabelParts {
  const combined = clean(input.combined);
  const rawMakeModel =
    [clean(input.make), clean(input.model)].filter(Boolean).join(' ') ||
    (combined && combined.toLowerCase() !== 'vehicle' ? combined : '');
  // Vehicle details require an actual make/model; a lone year is not "details" and falls back to code.
  const makeModel = rawMakeModel ? titleCaseVehicle(rawMakeModel) : '';
  const details = makeModel ? [clean(input.year), makeModel].filter(Boolean).join(' ').trim() : undefined;
  return {
    vin: clean(input.vin) || undefined,
    details,
    code: clean(input.programCode) || undefined,
    release: clean(input.release) || undefined,
  };
}

/** Ready-to-render parts: VIN, primary line (details, else year), and internal-only chips. */
export interface VehicleDisplay {
  vin?: string;
  primary?: string;
  code?: string;
  release?: string;
}

/** Applies the shared display contract (year fallback + internal-only code/release gating). */
export function vehicleDisplay(input: VehicleLabelInput, internal: boolean): VehicleDisplay {
  const { vin, details, code, release } = vehicleLabel(input);
  return {
    vin,
    primary: details ?? (clean(input.year) || undefined),
    code: internal ? code : undefined,
    release: internal ? release : undefined,
  };
}

/** Structural subset of the active vehicle identity that the context summary renders. */
export interface VehicleContextInput {
  /** Display name; folds the program code into the year when make/model are unknown. */
  name?: string | null;
  details?: string | null;
  vin?: string | null;
  program?: string | null;
  release?: string | null;
  /** Street trim of a launched VIN. Shown to every user; omitted when AVP has none. */
  trim?: string | null;
}

/** Vehicle context summary: one description line followed by the identifiers that qualify it. */
export interface VehicleContextParts {
  /**
   * Description line: "2027 Cadillac Lyriq", "2027 T1XX2_PU_LD", or the VIN when that is all the
   * identity carries. Empty only for an identity that carries neither a description nor a VIN.
   */
  primary: string;
  /** Whether `primary` is the VIN, so callers can render it as the identifier it is. */
  vinPrimary: boolean;
  /** VIN chip; omitted when the VIN is already the description line. */
  vin?: string;
  /** Program code chip; omitted for dealers and when the description already carries it. */
  code?: string;
  /** Engineering release chip; omitted for dealers. */
  release?: string;
  /** Street trim; omitted when the launched VIN has none. Shown to every user. */
  trim?: string;
}

/** Drops a trailing program code so external users never read one out of the name fallback. */
function withoutCode(name: string, code: string): string {
  return code && name.endsWith(code) ? name.slice(0, -code.length).trim() : name;
}

/**
 * Builds the vehicle context summary shown while a vehicle is loaded.
 *
 * One layout serves every lookup method: the description leads and its identifiers follow as chips,
 * so the top bar never reflows between a VIN, a vehicle and a program lookup. Program code and
 * engineering release are internal-only, so a dealer reads "2025 Cadillac Lyriq" however they got
 * there. Street trim travels with a launched VIN and is shown to every user. With no make/model
 * the description falls back to the name, which already reads
 * "2027 T1XX2_PU_LD" internally -- keeping the code on that line beats chipping it beside a bare year.
 */
export function vehicleContext(
  vehicle: VehicleContextInput,
  internal: boolean,
): VehicleContextParts {
  const vin = clean(vehicle.vin);
  const program = clean(vehicle.program);
  const code = internal ? program : '';
  const release = internal ? clean(vehicle.release) : '';
  const name = internal ? clean(vehicle.name) : withoutCode(clean(vehicle.name), program);
  // A VIN-only vehicle names itself after its VIN, so the description can be the VIN itself.
  const primary = clean(vehicle.details) || name || vin;
  // Requires a VIN of its own: an identity with nothing at all leaves `primary` empty, and empty is
  // not a VIN to style as one.
  const vinPrimary = Boolean(vin) && primary === vin;
  const trim = displayTrim(vehicle.trim);
  return {
    primary,
    vinPrimary,
    vin: vinPrimary ? undefined : vin || undefined,
    // Chip the code only when the description doesn't already spell it out.
    code: code && !primary.split(' ').includes(code) ? code : undefined,
    release: release || undefined,
    ...(trim ? { trim } : {}),
  };
}
